import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { HeartPulse } from "lucide-react";

/**
 * Renders the homepage hero media: image, MP4, YouTube embed, or Vimeo embed.
 *
 * Reads /api/hero on mount. While loading, shows a soft skeleton in the same
 * aspect-ratio so the page doesn't reflow. The component is fully responsive
 * and respects the admin-controlled flags (autoplay / muted / loop / controls).
 *
 * Falls back to the original Pexels image if the API is unreachable, so the
 * landing page never looks broken.
 */
const FALLBACK_IMAGE =
  "https://images.pexels.com/photos/7529492/pexels-photo-7529492.jpeg";

export default function HeroMedia() {
  const [hero, setHero] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/hero");
        if (!cancelled) setHero(data);
      } catch {
        if (!cancelled) setHero({ media_type: "image", image_url: FALLBACK_IMAGE });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const inner = !hero ? (
    <div className="w-full aspect-[5/6] sm:h-[500px] bg-emerald-50 animate-pulse" />
  ) : hero.enabled === false ? null : (
    <MediaSwitch hero={hero} />
  );

  if (hero && hero.enabled === false) return null;

  return (
    <div className="relative" data-testid="hero-media">
      <div className="absolute -inset-6 bg-gradient-to-br from-emerald-100/60 via-white to-amber-100/60 rounded-[2.5rem] blur-xl" />
      <div className="relative rounded-[2rem] overflow-hidden border border-emerald-100 shadow-xl bg-white">
        {inner}
        {hero?.badge_label || hero?.badge_subtitle ? (
          <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur rounded-2xl p-4 flex items-center gap-3 border border-white/70">
            <span className="w-10 h-10 rounded-full bg-emerald-700 text-white flex items-center justify-center shrink-0">
              <HeartPulse className="w-5 h-5" />
            </span>
            <div className="text-sm min-w-0">
              {hero.badge_label ? (
                <div className="font-semibold text-emerald-950">{hero.badge_label}</div>
              ) : null}
              {hero.badge_subtitle ? (
                <div className="text-emerald-900/70 line-clamp-2">{hero.badge_subtitle}</div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MediaSwitch({ hero }) {
  const t = hero.media_type;

  if (t === "video_upload" && hero.video_url) {
    return <UploadedVideo src={hero.video_url} hero={hero} />;
  }
  if (t === "youtube" && hero.youtube_embed_url) {
    return <Iframe src={hero.youtube_embed_url} title="Founder intro" />;
  }
  if (t === "vimeo" && hero.vimeo_embed_url) {
    return <Iframe src={hero.vimeo_embed_url} title="Founder intro" />;
  }
  return (
    <img
      src={hero.image_url || FALLBACK_IMAGE}
      alt={hero.headline || "Feel Greats founder"}
      loading="eager"
      className="w-full h-[420px] sm:h-[500px] object-cover"
      data-testid="hero-media-image"
    />
  );
}

function UploadedVideo({ src, hero }) {
  const ref = useRef(null);
  const webmSrc = hero.video_webm_url || "";

  // Pause when out of view so background tabs don't burn battery
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && hero.autoplay) {
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, [hero.autoplay]);

  // Order matters: list smaller/most-compatible first when available. WebM
  // (VP9 + Opus) is shipped alongside the H.264 master so browsers without
  // proprietary codec support (e.g. Linux Chromium / Playwright headless)
  // can still play the hero. Real Chrome/Safari/Firefox/Edge ship H.264 so
  // they'll just use the MP4.
  return (
    <video
      ref={ref}
      poster={hero.poster_url || undefined}
      autoPlay={hero.autoplay}
      muted={hero.muted}
      loop={hero.loop}
      controls={hero.show_controls}
      playsInline
      preload="metadata"
      className="w-full h-[420px] sm:h-[500px] object-cover bg-emerald-950"
      data-testid="hero-media-video"
    >
      {webmSrc ? (
        <source src={webmSrc} type="video/webm" data-testid="hero-media-video-webm" />
      ) : null}
      <source src={src} type="video/mp4" data-testid="hero-media-video-mp4" />
    </video>
  );
}

function Iframe({ src, title }) {
  return (
    <div className="relative w-full aspect-video sm:aspect-auto sm:h-[500px] bg-emerald-950">
      <iframe
        src={src}
        title={title}
        loading="lazy"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        className="absolute inset-0 w-full h-full"
        data-testid="hero-media-iframe"
      />
    </div>
  );
}
